import client from "./client";
import { ROLES, normalizeRoleScope, resolveRoleIdFromValue } from "../config/rbacConfig";
import { extractScopeSeed } from "../utils/scopeFields";

function toNullIfBlank(value) {
  if (value === undefined || value === null) return null;
  const text = String(value).trim();
  return text === "" ? null : text;
}

function normalizeBoolean(value, fallback = true) {
  if (value === undefined || value === null || value === "") return fallback;
  if (value === true || value === 1 || value === "1") return true;
  if (value === false || value === 0 || value === "0") return false;
  return Boolean(value);
}

function unwrapPayload(responseData) {
  const envelope = responseData ?? {};
  return envelope.data ?? envelope;
}

function resolveRoleMeta(user = {}) {
  const roles = Array.isArray(user.roles) ? user.roles : [];
  const primaryRole = roles[0] ?? user.role ?? {};
  const roleCode =
    user.role_code ??
    user.roleCode ??
    primaryRole.code ??
    primaryRole.backend_class ??
    primaryRole.backendClass ??
    primaryRole.name ??
    user.backend_class ??
    user.backendClass ??
    null;
  const roleId = resolveRoleIdFromValue(
    user.role_id ??
    user.roleId ??
    user.role ??
    user.roles?.[0] ??
    user.role_code ??
    user.roleCode ??
    user.role_name ??
    user.roleName ??
    user.role_label ??
    user.roleLabel ??
    primaryRole.id ??
    primaryRole.role_id ??
    primaryRole.roleId ??
    primaryRole.code ??
    primaryRole.label ??
    primaryRole.name ??
    primaryRole.backend_class ??
    primaryRole.backendClass ??
    roleCode
  );
  const role = roleId ? ROLES[roleId] : null;

  return {
    roleId,
    roleCode,
    roleLabel:
      user.role_name ??
      user.roleName ??
      user.role_label ??
      user.roleLabel ??
      primaryRole.label ??
      primaryRole.name ??
      role?.label ??
      roleCode ??
      null,
  };
}

function resolvePayloadRoleId(payload = {}) {
  const roles = Array.isArray(payload.roles) ? payload.roles : [];
  const primaryRole = roles[0] ?? payload.role ?? {};

  return resolveRoleIdFromValue(
    payload.role_id ??
    payload.roleId ??
    payload.role ??
    payload.roles?.[0] ??
    payload.role_code ??
    payload.roleCode ??
    payload.role_name ??
    payload.roleName ??
    payload.role_label ??
    payload.roleLabel ??
    payload.backend_class ??
    payload.backendClass ??
    primaryRole.id ??
    primaryRole.role_id ??
    primaryRole.roleId ??
    primaryRole.code ??
    primaryRole.label ??
    primaryRole.name ??
    primaryRole.backend_class ??
    primaryRole.backendClass ??
    null
  );
}

function normalizePayloadLocation(payload = {}) {
  const roleId = resolvePayloadRoleId(payload);
  const state = toNullIfBlank(payload.state);
  const lga = toNullIfBlank(payload.lga);

  if (roleId === 'R04') {
    return {
      state,
      lga,
    };
  }

  const normalizedScope = normalizeRoleScope(roleId, {
    level: payload.scope?.level ?? payload.level ?? payload.scope_level ?? payload.scopeLevel,
    state: payload.state,
    lga: payload.lga,
    ward: payload.ward,
  });

  return {
    state: normalizedScope.state ?? state,
    lga: normalizedScope.lga ?? lga,
  };
}

function normalizeUser(row = {}) {
  const institution = row.institution ?? {};
  const roleMeta = resolveRoleMeta(row);
  const rawScope = extractScopeSeed(row);
  // National roles should not inherit a local state/LGA value in the UI.
  // R04 keeps any LGA metadata for admin editing, but runtime scope remains state-based.
  const scope = normalizeRoleScope(roleMeta.roleId, rawScope);
  const locationScope = roleMeta.roleId === 'R04'
    ? {
        state: scope.state ?? rawScope.state ?? null,
        lga: rawScope.lga ?? row.lga ?? row.assignedLga ?? null,
      }
    : {
        state: scope.state ?? null,
        lga: scope.lga ?? null,
      };
  const moduleScope =
    row.module_scope ??
    row.moduleScope ??
    row.role?.module_scope ??
    row.role?.moduleScope ??
    (Array.isArray(row.roles) ? row.roles[0]?.module_scope ?? row.roles[0]?.moduleScope : null) ??
    null;
  const assignedModules = Array.isArray(row.assignedModules)
    ? row.assignedModules
    : Array.isArray(row.assigned_modules)
      ? row.assigned_modules
      : [];
  const roleAssignments = Array.isArray(row.role_assignments)
    ? row.role_assignments
    : Array.isArray(row.roleAssignments)
      ? row.roleAssignments
      : [];
  const roleAssignment = row.role_assignment ?? row.roleAssignment ?? null;
  const moduleAssignments = Array.isArray(row.module_assignments)
    ? row.module_assignments
    : Array.isArray(row.moduleAssignments)
      ? row.moduleAssignments
      : [];

  return {
    uuid: row.uuid ?? row.id ?? null,
    id: row.uuid ?? row.id ?? null,
    name: row.name ?? row.full_name ?? row.fullName ?? "",
    email: row.email ?? "",
    institution_uuid: institution.uuid ?? row.institution_uuid ?? row.institution_id ?? null,
    institution_label:
      institution.name ??
      row.institution_name ??
      row.institution_label ??
      row.institution_uuid ??
      row.institution_id ??
      "—",
    state: locationScope.state ?? null,
    lga: locationScope.lga ?? null,
    scope_level: scope.level ?? row.scope_level ?? null,
    is_active: normalizeBoolean(row.is_active ?? row.isActive, true),
    last_login: row.last_login ?? row.lastLogin ?? null,
    created_at: row.created_at ?? row.createdAt ?? null,
    updated_at: row.updated_at ?? row.updatedAt ?? null,
    roles: Array.isArray(row.roles) ? row.roles : [],
    role_id: roleMeta.roleId,
    role_code: roleMeta.roleCode,
    role_label: roleMeta.roleLabel,
    module_scope: moduleScope,
    moduleScope,
    assignedModules,
    assigned_modules: assignedModules,
    module_assignments: moduleAssignments,
    moduleAssignments,
    role_assignments: roleAssignments,
    roleAssignments,
    role_assignment: roleAssignment,
    roleAssignment,
  };
}

function extractUserList(responseData) {
  const payload = unwrapPayload(responseData);
  const items =
    Array.isArray(payload) ? payload :
    Array.isArray(payload.items) ? payload.items :
    Array.isArray(payload.results) ? payload.results :
    [];

  return items.map(normalizeUser);
}

function extractUserRecord(responseData) {
  const payload = unwrapPayload(responseData);
  if (Array.isArray(payload)) return normalizeUser(payload[0] ?? {});
  if (payload?.item) return normalizeUser(payload.item);
  if (payload?.items && Array.isArray(payload.items)) {
    return normalizeUser(payload.items[0] ?? {});
  }
  if (payload?.user) return normalizeUser(payload.user);
  return normalizeUser(payload);
}

function buildUserPayload(payload = {}) {
  const scope = normalizePayloadLocation(payload);

  return {
    name: toNullIfBlank(payload.name),
    email: toNullIfBlank(payload.email),
    password: toNullIfBlank(payload.password),
    institution_uuid: toNullIfBlank(payload.institution_uuid ?? payload.institution_id),
    state: scope.state,
    lga: scope.lga,
    is_active: normalizeBoolean(payload.is_active ?? payload.isActive, true),
  };
}

function buildUserUpdatePayload(payload = {}) {
  const next = {};
  const scope = normalizePayloadLocation(payload);

  if ("name" in payload) next.name = toNullIfBlank(payload.name);
  if ("email" in payload) next.email = toNullIfBlank(payload.email);
  if ("password" in payload) {
    const password = toNullIfBlank(payload.password);
    if (password) next.password = password;
  }
  if ("institution_uuid" in payload || "institution_id" in payload) {
    next.institution_uuid = toNullIfBlank(payload.institution_uuid ?? payload.institution_id);
  }
  if ("state" in payload) next.state = scope.state;
  if ("lga" in payload) next.lga = scope.lga;
  if ("is_active" in payload || "isActive" in payload) {
    next.is_active = normalizeBoolean(payload.is_active ?? payload.isActive, true);
  }

  return next;
}

export const userApi = {
  async me() {
    const { data } = await client.get("/v1/user");
    return extractUserRecord(data);
  },

  async list(params = {}) {
    const { data } = await client.get("/v1/users", { params });
    return extractUserList(data);
  },

  async get(uuid) {
    const { data } = await client.get(`/v1/users/${uuid}`);
    return extractUserRecord(data);
  },

  async create(payload) {
    const { data } = await client.post("/v1/users", buildUserPayload(payload));
    return extractUserRecord(data);
  },

  async update(uuid, payload) {
    const { data } = await client.put(`/v1/users/${uuid}`, buildUserUpdatePayload(payload));
    return extractUserRecord(data);
  },

  async remove(uuid) {
    const { data } = await client.delete(`/v1/users/${uuid}`);
    return unwrapPayload(data);
  },
};

export { normalizeUser, buildUserPayload };
